import React, { ReactElement, useEffect, useState } from 'react';
import { NumberFormatBase } from 'react-number-format';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import endpoints from '../../config/endpoints';
import globalEnum from '../../enums/globalEnum';
import { setLoading } from '../../services/reducers/homeSlice';
import restApi from '../../services/restApi';
import tools from '../../utils/tools';
import { Sidebar } from '../Layout/Sidebar';
import product from '../Product';
import ProductAccess from '../Role/ProductAccess';
import MapModal from './MapModal';

const UserManage = () => {
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<any>({products: [], categories: []});
  // Displayed comma-formatted via NumberFormatBase below; actual submission
  // (send() reads this whole form via native FormData) uses the parallel
  // hidden input so the backend still gets a plain numeric string.
  const [maxBuyAmount, setMaxBuyAmount] = useState(form?.maxBuyAmount ?? '');
  const [products, setProducts] = useState([]);
  const [cars, setCars] = useState([]);
  const [brands, setBrands] = useState([]);
  const [sign, setSign] = useState(1);
  const [categories, setCategories] = useState([]);
  const [roles, setRoles] = useState([]);
  const [address, setAddress] = useState<any>({phoneNumber: '',
    text: '',
    postal: '',
    cityId: 0,
    provinceId: 0});

  const send = async (e) => {
    e.preventDefault();

    const formData = new FormData(e.target);

    // Client-side guard before this ever hits the network — phoneNumber is
    // required on both create/edit (NOT NULL column), password is required
    // only when creating a brand-new user (there's no existing password to
    // fall back to). Submitting either blank used to reach the backend and
    // crash it instead of showing a clean message.
    const phoneNumberValue = String(formData.get('phoneNumber') || '').trim();
    const passwordValue = String(formData.get('password') || '').trim();
    const nameValue = String(formData.get('name') || '').trim();

    if (!nameValue) {
      Swal.fire({ title: 'اطلاعات ناقص', text: 'وارد کردن نام الزامی است', icon: 'warning', confirmButtonText: 'متوجه شدم' });
      return;
    }
    if (!phoneNumberValue) {
      Swal.fire({ title: 'اطلاعات ناقص', text: 'وارد کردن شماره تلفن الزامی است', icon: 'warning', confirmButtonText: 'متوجه شدم' });
      return;
    }
    if (!paramId && !passwordValue) {
      Swal.fire({ title: 'اطلاعات ناقص', text: 'برای ساخت کاربر جدید وارد کردن رمز عبور الزامی است', icon: 'warning', confirmButtonText: 'متوجه شدم' });
      return;
    }
    if (!formData.get('role')) {
      Swal.fire({ title: 'اطلاعات ناقص', text: 'انتخاب نقش کاربری الزامی است', icon: 'warning', confirmButtonText: 'متوجه شدم' });
      return;
    }

    dispatch(setLoading(true));

    const res = await restApi(endpoints.user.basic + (paramId || '')).post({
      name: formData.get('name'),
      lastName: formData.get('lastName'),
      nationalCode: formData.get('nationalCode'),
      phoneNumber: formData.get('phoneNumber'),
      password: formData.get('password'),
      email: formData.get('email'),
      bankCard: formData.get('bankCard'),
      roleId: formData.get('role'),
      status: formData.get('status'),
      username: formData.get('username'),
      specialPercent: Number(formData.get('specialPercent')) * sign,
      userType: formData.get('userType'),
      maxBuyAmount: formData.get('maxBuyAmount'),
      address: {
        cityId: formData.get('addressCity'),
        provinceId: formData.get('addressProvince'),
        text: formData.get('addressText'),
        postalCode: formData.get('addressPostal'),
        phone: formData.get('addressPhone'),
      }
    });

    const result = await restApi(endpoints.user.access + res.data?.id).post({
      cars: form?.selectedCars,
      brands: form?.selectedBrands,
      categories: form?.categories,
      products: form?.products
    })

    if (res.code == 200) {
      Swal.fire({
        title: 'موفق',
        text: `کاربر با موفقیت ${paramId ? 'ویرایش' : 'ساخته'} شد`,
        icon: 'success',
        confirmButtonText: 'متوجه شدم',
        didClose() {
          navigate('/user')
        }
      });
    } else {
      Swal.fire({
        title: 'ناموفق',
        text: res?.data,
        icon: 'error',
        confirmButtonText: 'متوجه شدم'
      });
    }

    dispatch(setLoading(false));
  };


  const fetchData = async () => {
    dispatch(setLoading(true));

    const res = await Promise.all([
      restApi(endpoints.product.index).get(),
      restApi(endpoints.role.index).get(),
      restApi(endpoints.car.index, true).get(),
      restApi(endpoints.brand.index, true).get(),
      restApi(endpoints.category.index, true).get(),
      paramId && restApi(endpoints.user.single + paramId).get(),
    ]);
    setProducts(res[0].data)
    setRoles(res[1].data);
    setCars(res[2].data);
    setBrands(res[3].data);
    setCategories(res[4].data);
    if (paramId) {
      setForm({ ...res[5].data,
        products: [...res[5].data?.accesses?.filter(e => e.entityType == globalEnum.entityType.product).map(e => ({ productId: e.entityId, status: e.status, price: e.price, unitType: e.unitType, allowUnitTypeFallback: e.allowUnitTypeFallback})), ...res[0].data?.filter(e => !res[5].data?.accesses?.filter(e => e.entityType == globalEnum.entityType.product).map(e => e.entityId).includes(e.id)).map(e => ({ productId: e.id, status: 0, price: e.price, unitType: e.unitType}))],
        categories: res[5].data?.accesses?.filter(e => e.entityType == globalEnum.entityType.category).map(e => ({ categoryId: e.entityId, status: e.status, price: e.price})),
        selectedCars: res[5].data?.accesses?.filter(e => e.entityType == globalEnum.entityType.car).map(e => e.entityId.toString()),
        selectedCategories: res[5].data?.accesses?.filter(e => e.entityType == globalEnum.entityType.category).map(e => e.entityId.toString()),
        selectedBrands: res[5].data?.accesses?.filter(e => e.entityType == globalEnum.entityType.brand).map(e => e.entityId.toString()),
      });
      setSign(res[5].data?.specialPercent > 0 ? 1 : -1)
      setAddress(res[5].data?.address);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // form loads asynchronously in edit mode (fetchData above); keep the
  // display-only maxBuyAmount state in sync once it arrives.
  useEffect(() => {
    setMaxBuyAmount(form?.maxBuyAmount ?? '');
  }, [form?.maxBuyAmount]);

  return (
    <>
      <body className="dashboardBody">
      <Sidebar/>
      <main className="dashBoardMain main">
        <form onSubmit={send}>

        <div className="addInfoHeader">
          <button className="dashboardHeader keepRight clickable" type="submit" >
            ارسال
          </button>
          <span>
            <h1 className="sideBarTitle"> بازگشت به صفحه کاربران</h1>
             <h1 className="dashBoardTitle">{paramId ? 'ویرایش کاربر' : 'ایجاد حساب کاربری'}</h1>
          </span>
          <i className="backAdd clickable" onClick={() => navigate('/user/')}></i>
        </div>
        <section className="userInfo">
          <h1 className="dashBoardTitle">اطلاعات کاربر</h1>
          <div className="userInfoContainer width96">
            <div className="section30">
              <label className="sideBarTitle">کد ملی</label>
              <input className="editProductInput" defaultValue={form?.nationalCode} name="nationalCode"/>
              <label className="sideBarTitle">شماره کارت بانکی</label>
              <input className="editProductInput" defaultValue={form?.bankCard} name="bankCard"/>
              <label className="sideBarTitle">رمز عبور <small>(فقط در صورت نیاز به تغییر وارد کنید)</small></label>
              <input className="editProductInput" name="password"/>
              <div>
                <div className="column">
                  <label className="sideBarTitle">وضعیت مشاهده قیمت</label>
                  <span className="radioButtons">
                <input className="smallInput" placeholder="0%" type="number" defaultValue={form?.specialPercent < 0 ? form?.specialPercent * -1 : form?.specialPercent} name="specialPercent"/>
                  <span className="radioButtons">
                    <p>بالاتر</p>
                      <i className={`${sign === 1 ? 'slectedradioButton' : 'radioButton'}`} onClick={() => setSign(1)}></i>
                    <hr className="verLine"/>
                    <p>پایین تر</p>
                    <i className={`${sign === -1 ? 'slectedradioButton' : 'radioButton'}`} onClick={() => setSign(-1)}></i>
                    </span>
                      </span>
                </div>
              </div>
            </div>
            <div className="section30">
              <label className="sideBarTitle">نام کاربری</label>
              <input className="editProductInput" defaultValue={form?.username} name="username"/>
              <label className="sideBarTitle">شماره تلفن</label>
              <input className="editProductInput" defaultValue={form?.phoneNumber} name="phoneNumber"/>
              <label className="sideBarTitle">ایمیل</label>
              <input className="editProductInput" defaultValue={form?.email} name="email"/>
              <label className="sideBarTitle">سقف خرید</label>
              <input type="hidden" name="maxBuyAmount" value={maxBuyAmount}/>
              <NumberFormatBase
                className="editProductInput"
                value={maxBuyAmount}
                onValueChange={(values) => setMaxBuyAmount(values.value)}
                format={(str) => (str ? tools.formatPrice(Number(str)) : '')}
              />
            </div>
            <div className="section30">
              <label className="sideBarTitle">وضعیت</label>
              <select className="selector30"  defaultValue={form?.status} name="status" >
                <option value={0}>غیر فعال</option>
                <option value={1} selected={form?.status == 1}>فعال</option>
              </select>
              <label className="sideBarTitle">نوع کاربر</label>
              <select className="selector30" name="userType" >
                <option value='company' selected={form?.userType == 'company'}>شرکتی</option>
                <option value='agent' selected={form?.userType == 'agent'}>نماینده</option>
                <option value='other' selected={form?.userType == 'other'}>متفرقه</option>
              </select>
              <label className="sideBarTitle">نقش کاربری</label>
              <select className="selector30" value={roles?.find(e => e.slug == form?.role)?.id} name="role" onChange={(input) => setForm(prev => ({
                ...prev,
                roleId: input.target.value,
                role: roles.find(e => e.id == input.target.value).slug
                // maxBuyAmount: roles.find(e => e.id == input.target.value)?.maxBuyAmount,
                // products: roles.find(e => e.id == input.target.value)?.accesses?.filter(e => e.entityType == globalEnum.entityType.product).map(e => ({ productId: e.productId, status: e.status, price: e.price, unitType: e.unitType})),
                // selectedCategories: roles.find(e => e.id == input.target.value)?.accesses?.filter(e => e.entityType == globalEnum.entityType.category).map(e => e.entityId.toString()),
                // selectedBrands: roles.find(e => e.id == input.target.value)?.accesses?.filter(e => e.entityType == globalEnum.entityType.brand).map(e => e.entityId.toString()),
                // selectedCars: roles.find(e => e.id == input.target.value)?.accesses?.filter(e => e.entityType == globalEnum.entityType.car).map(e => e.entityId.toString()),
              })) }>
                {roles.map(e => <option value={e.id}>{e.title}</option>)}
              </select>
              <label className="sideBarTitle">نام و نام خانوادگی</label>
              <input className="editProductInput" defaultValue={form?.name} name="name"/>
              </div>
          </div>
        </section>
        <section className="bottom">
          <h6 className="dashBoardTitle">آدرس ها</h6>
          <div className="bottomDiv">
            <span className="adressCard">
          <div className="addressCardRight">
            <span className="adressPin">
              <i className="mapPin"></i>
              <h6>{address?.title}</h6>
            </span>
             <p className="adressText marginZero">{address?.text}
               <br/>{address?.phoneNumber}</p>
          </div>
          <div className="addressImage">
          <img src="/img/map.jpg" className="mapPhoto"/>
        <span className="svgContainer">
            {/* <i className="trash"></i> */}
              <i className="edit clickable" onClick={() => setShowModal(true)}></i>
        </span>
            </div>
        </span>
            <div className={`formModal ${showModal ? 'flex' : ''}`}>
              <div className="modalOverLay" onClick={() => setShowModal(false)}>
              </div>
              <MapModal setShowModal={setShowModal} address={address} setAddress={setAddress}/>
            </div>
          </div>
        </section>
         <ProductAccess categories={categories} cars={cars} brands={brands} form={form} setForm={setForm} products={products} />
        </form>
      </main>
      </body>
    </>
  );
};
export default UserManage;